import React, { useMemo, useState } from "react";
import notifications from "../../../common/Components/Notifications/Notification";
import { savePunch } from "../../../services/karkhanaApi";
import { enqueue, newRequestId } from "../rounds/entryQueue";
import type { Person } from "../rounds/PersonPicker";
import SearchField from "../ui/SearchField";
import QuickAddPerson from "../people/QuickAddPerson";
import { useKT } from "../i18n/kt";
import { nowHHMM, stateOf } from "./punchModel";

type Marked = { at: string; queued?: boolean };

/**
 * Mornings: weavers and helpers arrive together. Everyone not in yet is listed; one tap on "In"
 * marks a person in at the chosen time with their usual work, or tick several and mark them all
 * at once. Without signal each tap is kept on the phone and sent later.
 */
const ArrivalBoard = ({
  date,
  isToday,
  people,
  roster,
  queueKey,
  onMarked,
}: {
  date: string;
  isToday: boolean;
  /** Today's attendance rows (people with punches). */
  people: any[];
  roster: Person[];
  queueKey: string;
  onMarked: () => void;
}) => {
  const { t } = useKT();
  const [time, setTime] = useState(isToday ? nowHHMM() : "09:00");
  const [useNow, setUseNow] = useState(isToday);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  // Not found by search: added here, then marked in like everyone else.
  const [adding, setAdding] = useState<string | null>(null);

  const [marked, setMarked] = useState<Record<string, Marked>>({});
  const inNow = useMemo(() => new Set(people.filter((p) => stateOf(p) === "IN").map((p) => p.personId)), [people]);
  // Anyone marked on this screen stays listed with a tick, so the supervisor sees it took.
  const waiting = useMemo(() => {
    const term = q.trim().toUpperCase().replace(/[\s_-]/g, "");
    return roster
      .filter((p) => !inNow.has(p.id) || marked[p.id])
      .filter((p) => !term || p.code.toUpperCase().replace(/[\s_-]/g, "").includes(term) || (p.name || "").toUpperCase().includes(term))
      .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
  }, [roster, inNow, q, marked]);

  const at = () => (useNow && isToday ? nowHHMM() : time);

  const markIn = async (ids: string[]) => {
    if (!ids.length) return;
    const when = at();
    if (!when) {
      notifications({ title: t("Enter the time"), message: t("When did they come in?"), type: "warning" });
      return;
    }
    setBusy(true);
    let ok = 0;
    const failed: string[] = [];
    for (const id of ids) {
      const person = roster.find((p) => p.id === id);
      const body = { personId: id, date, time: when, type: "IN", clientRequestId: newRequestId() };
      try {
        const res: any = await savePunch(body);
        if (res?.success) {
          ok++;
          setMarked((m) => ({ ...m, [id]: { at: when } }));
        } else {
          failed.push(`${person?.code || ""}: ${t(res?.message || "Not saved")}`);
        }
      } catch {
        // No signal: keep it with the time it was tapped and send it later.
        enqueue(queueKey, { clientRequestId: body.clientRequestId, body: { ...body, kind: "punch" }, loomCode: person?.code || "", queuedAt: new Date().toISOString() });
        ok++;
        setMarked((m) => ({ ...m, [id]: { at: when, queued: true } }));
      }
    }
    setBusy(false);
    setPicked(new Set());
    if (failed.length) notifications({ title: t("Some not marked"), message: failed.join(" · "), type: "warning" });
    else if (ok > 1) notifications({ title: t("Marked in"), message: t("{{n}} people in at {{time}}", { n: ok, time: when }), type: "success" });
    onMarked();
  };

  const toggle = (id: string) =>
    setPicked((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const pending = waiting.filter((p) => !marked[p.id]);

  return (
    <div className="ka-board">
      <div className="ka-board__time">
        <span className="kr-label">{t("Time in")}</span>
        <div className="kr-chips">
          {isToday ? (
            <button type="button" className={`kr-choice${useNow ? " is-on" : ""}`} onClick={() => setUseNow(true)}>
              {t("Now")}
            </button>
          ) : null}
          <input
            type="time"
            className={`ka-time${!useNow || !isToday ? " is-on" : ""}`}
            value={time}
            onChange={(e) => {
              setTime(e.target.value);
              setUseNow(false);
            }}
            aria-label={t("Time in")}
          />
        </div>
      </div>

      <SearchField value={q} onChange={setQ} placeholder={t("Find someone, e.g. J12")} label={t("Find someone")} autoCapitalize="characters" count={`${pending.length} ${t("not in yet")}`} />

      <div className="ka-list">
        {waiting.map((p) => {
          const m = marked[p.id];
          return (
            <div key={p.id} className={`ka-row${m ? " is-done" : ""}${picked.has(p.id) ? " is-picked" : ""}`}>
              {!m ? (
                <input type="checkbox" className="ka-check" checked={picked.has(p.id)} onChange={() => toggle(p.id)} aria-label={`${t("Select")} ${p.code}`} />
              ) : (
                <span className="ka-check" aria-hidden="true" />
              )}
              <button type="button" className="ka-who" onClick={() => !m && toggle(p.id)} disabled={!!m}>
                <span className="kw-code">{p.code}</span> <span className="ka-name">{p.name}</span>
              </button>
              {m ? (
                <span className="ka-done">
                  ✓ {t("In")} {m.at}
                  {m.queued ? ` · ${t("on this phone")}` : ""}
                </span>
              ) : (
                <button type="button" className="ka-in" disabled={busy} onClick={() => markIn([p.id])}>
                  {t("In")}
                </button>
              )}
            </div>
          );
        })}
        {!waiting.length ? <div className="kw-empty">{q ? t("Nobody by that code or name.") : t("Everyone is in.")}</div> : null}
        {q.trim() && !/^[A-Za-z]{1,4}[_-]?\d+$/.test(q.trim()) ? (
          <button type="button" className={`kfa__add${waiting.length ? "" : " is-main"}`} onClick={() => setAdding(q.trim())}>
            + {t("Add “{{name}}” as a new person", { name: q.trim() })}
          </button>
        ) : null}
      </div>

      {picked.size ? (
        <div className="ka-bulk">
          <button type="button" className="kh-btn" onClick={() => setPicked(new Set())}>
            {t("Clear")}
          </button>
          <button type="button" className="ka-bulk__go" disabled={busy} onClick={() => markIn(Array.from(picked))}>
            {busy ? t("Saving…") : t("Mark {{n}} in at {{time}}", { n: picked.size, time: useNow && isToday ? t("now") : time })}
          </button>
        </div>
      ) : pending.length > 1 ? (
        <div className="ka-bulk">
          <button type="button" className="kh-btn" onClick={() => setPicked(new Set(pending.map((p) => p.id)))}>
            {t("Select all {{n}}", { n: pending.length })}
          </button>
        </div>
      ) : null}
      {adding !== null ? (
        <QuickAddPerson
          initialName={adding}
          role="HELPER"
          title={t("New person")}
          submitLabel={t("Add & mark in")}
          onCancel={() => setAdding(null)}
          onDone={async (p) => {
            setAdding(null);
            setQ("");
            await markIn([p.id]);
          }}
        />
      ) : null}
    </div>
  );
};

export default ArrivalBoard;
